import React, { useState } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Button,
  Alert,
  CircularProgress,
  Typography,
  Box,
  List,
  ListItem,
  ListItemText,
  ListItemSecondaryAction,
  IconButton,
  Chip,
  Divider,
  MenuItem,
} from '@mui/material';
import { Cancel as CancelIcon } from '@mui/icons-material';
import { isToday } from 'date-fns';
import { useTranslation } from 'react-i18next';
import { formatDate } from '@/utils/dateUtils';
import { useJobs, useJoinChannels, useCancelJob, useSyncAccounts } from '@/hooks/useSync';
import { getErrorMessage } from '@/api/client';
import { JOIN_JOB_TYPE, JoinResult, MAX_JOIN_LINKS, SyncJob, isJobActive } from '@/api/sync';

interface JoinChannelDialogProps {
  open: boolean;
  onClose: () => void;
}

/**
 * `estimated_at` carries the server's UTC offset like every other /sync
 * timestamp, so `new Date(...)` parses it right in any browser timezone.
 * Anything past today keeps its date so a queue that spills into tomorrow
 * doesn't read as "in a few minutes". That date is numeric on purpose: a
 * localized month name puts Arabic letters inside the dir="ltr" run, and bidi
 * then reorders the whole string — 'MMM d, HH:mm' under `ar` renders
 * "أغسطس 19, 00:05" as time, day, month with an orphaned comma.
 */
function formatEta(iso: string): string {
  const eta = new Date(iso);
  if (Number.isNaN(eta.getTime())) return iso;
  return isToday(eta) ? `~${formatDate(eta, 'HH:mm')}` : `~${formatDate(eta, 'yyyy-MM-dd HH:mm')}`;
}

// scraper_accounts.status (migration 033) — an empty-but-'fresh' account is the
// one the picker must not make look like the safest choice.
const ACCOUNT_STATUS_COLOR: Record<string, 'default' | 'success' | 'warning' | 'error'> = {
  active: 'success',
  fresh: 'warning',
  needs_login: 'warning',
  banned: 'error',
  retired: 'default',
};

const JoinChannelDialog: React.FC<JoinChannelDialogProps> = ({ open, onClose }) => {
  const { t } = useTranslation('pages');
  const joinMutation = useJoinChannels();
  const cancelMutation = useCancelJob();
  // Fetched with the dialog, not on mount: nothing else on the chat page needs
  // the account list.
  const {
    data: accounts = [],
    isLoading: accountsLoading,
    isError: accountsError,
  } = useSyncAccounts(open);

  const [links, setLinks] = useState('');
  const [error, setError] = useState('');
  const [result, setResult] = useState<JoinResult | null>(null);
  const [pickedAccountId, setPickedAccountId] = useState<number | null>(null);

  // Derived, not stored in an effect: the list arrives after the first render,
  // so a state-only selection would be empty for that paint. An explicit pick
  // wins, but only while it is still in the list — the dialog keeps its pick
  // across opens and an account can be disabled meanwhile. Otherwise the
  // backend's default account, else the first one.
  const defaultAccountId = accounts.find((a) => a.is_default)?.id ?? accounts[0]?.id ?? null;
  const selectedAccountId = accounts.some((a) => a.id === pickedAccountId)
    ? pickedAccountId
    : defaultAccountId;
  const selectedAccount = accounts.find((a) => a.id === selectedAccountId) ?? null;

  // Poll only while the dialog is open — this component stays mounted on the
  // chat page, and a queued batch would otherwise keep the fast poll running
  // for days in the background. Held until an account is known: without one the
  // filter would drop and the first paint would show every account's queue,
  // not the queue the next submit will land in.
  const { data: jobs = [], isError: jobsError } = useJobs(
    JOIN_JOB_TYPE,
    50,
    open && selectedAccountId != null,
    selectedAccountId ?? undefined
  );

  // The batch is echoed back with the account it actually landed on — resolved
  // to a label, never inferred from the current pick.
  const accountLabelOf = (id: number) => accounts.find((a) => a.id === id)?.label ?? `#${id}`;

  const pendingJobs: SyncJob[] = jobs.filter(isJobActive);

  const linkList = links
    .split('\n')
    .map((l) => l.trim())
    .filter(Boolean);

  const handleEnqueue = async () => {
    setError('');
    setResult(null);
    if (!linkList.length || !selectedAccountId) return;
    if (linkList.length > MAX_JOIN_LINKS) {
      // Guard client-side: the backend 400s the WHOLE batch past the cap, in
      // English — better to refuse here, translated, with nothing lost.
      setError(t('chatPreview.joinChannel.tooMany', { count: linkList.length, max: MAX_JOIN_LINKS }));
      return;
    }
    try {
      const res = await joinMutation.mutateAsync({ links: linkList, accountId: selectedAccountId });
      setLinks('');
      setResult(res);
    } catch (err: unknown) {
      setError(getErrorMessage(err));
    }
  };

  const handleCancel = async (jobId: number) => {
    try {
      await cancelMutation.mutateAsync(jobId);
    } catch {
      // The row may have been claimed between the poll and the click; the next
      // poll shows its real state.
    }
  };

  const handleClose = () => {
    setLinks('');
    setError('');
    setResult(null);
    onClose();
  };

  return (
    <Dialog open={open} onClose={handleClose} maxWidth="sm" fullWidth>
      <DialogTitle>{t('chatPreview.joinChannel.title')}</DialogTitle>
      <DialogContent>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
          {t('chatPreview.joinChannel.help', { max: MAX_JOIN_LINKS })}
        </Typography>

        {error && (
          <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError('')}>
            {error}
          </Alert>
        )}

        {result && result.queued.length > 0 && (
          <Alert severity="success" sx={{ mb: 2 }}>
            {t('chatPreview.joinChannel.queued', { count: result.queued.length })}{' '}
            {t('chatPreview.joinChannel.queuedAccount')}{' '}
            <Box component="span" dir="ltr">
              {accountLabelOf(result.account_id)}
            </Box>
          </Alert>
        )}
        {result && result.duplicates.length > 0 && (
          <Alert severity="info" sx={{ mb: 2 }}>
            {t('chatPreview.joinChannel.duplicates', { count: result.duplicates.length })}
            <Box component="span" dir="ltr" sx={{ display: 'block', fontFamily: 'monospace', fontSize: '0.75rem', wordBreak: 'break-all' }}>
              {result.duplicates.join(', ')}
            </Box>
          </Alert>
        )}
        {result && result.blocked_by_dedup.length > 0 && (
          <Alert severity="warning" sx={{ mb: 2 }}>
            {t('chatPreview.joinChannel.blocked', { count: result.blocked_by_dedup.length })}
            <Box component="span" dir="ltr" sx={{ display: 'block', fontFamily: 'monospace', fontSize: '0.75rem', wordBreak: 'break-all' }}>
              {result.blocked_by_dedup.join(', ')}
            </Box>
          </Alert>
        )}
        {result && result.errors.length > 0 && (
          <Alert severity="error" sx={{ mb: 2 }}>
            {t('chatPreview.joinChannel.errors', { count: result.errors.length })}
            <Box component="span" dir="ltr" sx={{ display: 'block', fontFamily: 'monospace', fontSize: '0.75rem', wordBreak: 'break-all', whiteSpace: 'pre-line' }}>
              {result.errors.map((e) => `${e.link}: ${e.reason}`).join('\n')}
            </Box>
          </Alert>
        )}

        {accountsError && (
          <Alert severity="error" sx={{ mb: 2 }}>
            {t('chatPreview.joinChannel.accountsError')}
          </Alert>
        )}

        <TextField
          select
          fullWidth
          size="small"
          label={t('chatPreview.joinChannel.accountLabel')}
          value={selectedAccountId ?? ''}
          onChange={(e) => setPickedAccountId(Number(e.target.value))}
          disabled={accountsLoading || joinMutation.isPending}
          helperText={accountsLoading ? t('chatPreview.joinChannel.accountsLoading') : undefined}
          sx={{ mb: 1 }}
        >
          {accounts.map((acct) => (
            <MenuItem
              key={acct.id}
              value={acct.id}
              title={t('chatPreview.joinChannel.accountOption', {
                label: acct.label,
                count: acct.channel_count,
                limit: acct.channel_limit,
                headroom: acct.headroom,
              })}
            >
              <Box sx={{ display: 'flex', alignItems: 'baseline', gap: 1, minWidth: 0 }}>
                <Box component="span" sx={{ overflow: 'hidden', textOverflow: 'ellipsis' }}>
                  {acct.label}
                </Box>
                <Chip
                  size="small"
                  variant="outlined"
                  color={ACCOUNT_STATUS_COLOR[acct.status] ?? 'default'}
                  label={t(`chatPreview.joinChannel.accountStatus.${acct.status}`, {
                    defaultValue: acct.status,
                  })}
                />
                <Typography component="span" variant="caption" color="text.secondary">
                  {/* The count fraction is its own LTR run: left to the bidi
                      algorithm the RTL layout renders "496 / 500" reversed,
                      because the spaced slash between two numbers resolves RTL. */}
                  <Box component="span" dir="ltr">
                    {acct.channel_count} / {acct.channel_limit}
                  </Box>{' '}
                  {t('chatPreview.joinChannel.accountChannels')} ·{' '}
                  {t('chatPreview.joinChannel.accountHeadroom', { count: acct.headroom })}
                </Typography>
              </Box>
            </MenuItem>
          ))}
        </TextField>

        {/* D1 stated outright: the account that joins is the account that will
            scrape. Nothing else on this screen implies it. */}
        <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 1 }}>
          {t('chatPreview.joinChannel.accountHelp')}
        </Typography>

        {selectedAccount && (
          <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 2 }}>
            {t('chatPreview.joinChannel.lastVerified')}{' '}
            {selectedAccount.last_verified_at ? (
              // Numeric format on purpose: it is pure digits, so dir="ltr" is
              // unambiguous in the RTL layout.
              <Box component="span" dir="ltr">
                {formatDate(selectedAccount.last_verified_at, 'yyyy-MM-dd HH:mm')}
              </Box>
            ) : (
              t('chatPreview.joinChannel.neverVerified')
            )}
            {' · '}
            {t('chatPreview.joinChannel.lastConnected')}{' '}
            {selectedAccount.last_connected_at ? (
              <Box component="span" dir="ltr">
                {formatDate(selectedAccount.last_connected_at, 'yyyy-MM-dd HH:mm')}
              </Box>
            ) : (
              // Guarded, not formatted: new Date(null) is 1970-01-01, a wrong
              // answer that looks like a real one.
              t('chatPreview.joinChannel.neverConnected')
            )}
          </Typography>
        )}

        {selectedAccount && selectedAccount.headroom === 0 && (
          <Alert severity="warning" sx={{ mb: 2 }}>
            {t('chatPreview.joinChannel.accountFull', { limit: selectedAccount.channel_limit })}
          </Alert>
        )}
        {selectedAccount && selectedAccount.headroom > 0 && linkList.length > selectedAccount.headroom && (
          <Alert severity="warning" sx={{ mb: 2 }}>
            {t('chatPreview.joinChannel.accountNotEnough', {
              count: linkList.length,
              headroom: selectedAccount.headroom,
            })}
          </Alert>
        )}

        <TextField
          autoFocus
          fullWidth
          multiline
          minRows={3}
          label={t('chatPreview.joinChannel.linksLabel')}
          placeholder="https://t.me/channel1&#10;https://t.me/channel2&#10;@username"
          value={links}
          onChange={(e) => setLinks(e.target.value)}
          disabled={joinMutation.isPending}
          dir="ltr"
          sx={{ mb: 2 }}
        />

        {/* Pending queue */}
        {jobsError ? (
          <Box>
            <Divider sx={{ mb: 1 }} />
            <Alert severity="error">{t('chatPreview.joinChannel.jobsError')}</Alert>
          </Box>
        ) : pendingJobs.length > 0 && (
          <Box>
            <Divider sx={{ mb: 1 }} />
            <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
              {t('chatPreview.joinChannel.pendingQueue')} ({pendingJobs.length})
            </Typography>
            <List dense disablePadding>
              {pendingJobs.map((job) => (
                <ListItem key={job.id} sx={{ px: 0 }}>
                  <ListItemText
                    primary={
                      <Typography variant="body2" dir="ltr" sx={{ wordBreak: 'break-all' }}>
                        {String(job.params?.source ?? job.id)}
                      </Typography>
                    }
                    secondary={
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mt: 0.5 }}>
                        <Chip
                          label={t(`sync.status.${job.display_status}`)}
                          size="small"
                          color={job.status === 'running' ? 'warning' : 'default'}
                          variant="outlined"
                        />
                        {job.estimated_at ? (
                          <Typography variant="caption" color="text.secondary" dir="ltr">
                            {formatEta(job.estimated_at)}
                          </Typography>
                        ) : job.status === 'queued' && (
                          // null ETA on a queued join means either joins are
                          // disabled by config (join_daily_limit <= 0) or the
                          // row left the queued set between the backend's two
                          // SELECTs. The backend cannot tell those apart, so
                          // the copy states the fact and only offers the cause.
                          <Typography variant="caption" color="warning.main">
                            {t('chatPreview.joinChannel.noEta')}
                          </Typography>
                        )}
                      </Box>
                    }
                  />
                  {/* Only queued rows: a running join is already in flight at
                      Telegram, so cancelling it would not undo the join. */}
                  {job.status === 'queued' && (
                    <ListItemSecondaryAction>
                      <IconButton
                        size="small"
                        onClick={() => handleCancel(job.id)}
                        disabled={cancelMutation.isPending && cancelMutation.variables === job.id}
                        title={t('chatPreview.joinChannel.cancelItem')}
                      >
                        <CancelIcon fontSize="small" />
                      </IconButton>
                    </ListItemSecondaryAction>
                  )}
                </ListItem>
              ))}
            </List>
          </Box>
        )}
      </DialogContent>
      <DialogActions>
        <Button onClick={handleClose} disabled={joinMutation.isPending}>
          {t('chatPreview.joinChannel.cancel')}
        </Button>
        <Button
          onClick={handleEnqueue}
          variant="contained"
          disabled={!links.trim() || joinMutation.isPending || accountsLoading || !selectedAccountId}
          startIcon={joinMutation.isPending ? <CircularProgress size={20} /> : null}
        >
          {t('chatPreview.joinChannel.submit')}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default JoinChannelDialog;
